import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [travelers, setTravelers] = useState('2');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [category, setCategory] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const navigate = useNavigate();

  const travelerOptions = [
    { value: '1', label: '1 Traveler' },
    { value: '2', label: '2 Travelers' },
    { value: '3', label: '3 Travelers' },
    { value: '4', label: '4 Travelers' },
    { value: '5', label: '5+ Travelers' }
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'adventure', label: 'Adventure & Outdoor' },
    { value: 'cultural', label: 'Cultural & Historical' },
    { value: 'food', label: 'Food & Drink' },
    { value: 'nature', label: 'Nature & Wildlife' },
    { value: 'city', label: 'City Tours' },
    { value: 'beach', label: 'Beach & Water Sports' }
  ];

  const priceRangeOptions = [
    { value: '', label: 'Any Price' },
    { value: '0-100', label: 'Under $100' },
    { value: '100-300', label: '$100 - $300' },
    { value: '300-500', label: '$300 - $500' },
    { value: '500+', label: '$500+' }
  ];

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    
    setCheckInDate(today?.toISOString()?.split('T')?.[0]);
    setCheckOutDate(tomorrow?.toISOString()?.split('T')?.[0]);
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    const searchParams = new URLSearchParams();
    
    if (searchQuery) searchParams?.set('q', searchQuery);
    if (checkInDate) searchParams?.set('checkIn', checkInDate);
    if (checkOutDate) searchParams?.set('checkOut', checkOutDate);
    if (travelers) searchParams?.set('travelers', travelers);
    if (category) searchParams?.set('category', category);
    if (priceRange) searchParams?.set('price', priceRange);
    
    navigate(`/search-results?${searchParams?.toString()}`);
  };

  return (
    <section className="relative min-h-[600px] lg:min-h-[700px] bg-gradient-to-br from-primary via-primary/90 to-accent overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-white/20 rounded-full blur-3xl"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in">
            Discover Your Next
            <span className="block text-secondary">Adventure</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 animate-slide-up">
            Explore breathtaking destinations, book unforgettable experiences, and create memories that last a lifetime
          </p>
        </div>

        {/* Search Form */}
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSearch} className="bg-white rounded-2xl shadow-2xl p-6 lg:p-8 animate-scale-in">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Destination Search */}
              <div className="lg:col-span-2">
                <Input
                  label="Where to?"
                  type="text"
                  placeholder="Search destinations, tours, activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full"
                />
              </div>

              {/* Check-in Date */}
              <div>
                <Input
                  label="Check-in"
                  type="date"
                  value={checkInDate}
                  onChange={(e) => setCheckInDate(e?.target?.value)}
                  className="w-full"
                />
              </div>

              {/* Check-out Date */}
              <div>
                <Input
                  label="Check-out"
                  type="date"
                  value={checkOutDate}
                  onChange={(e) => setCheckOutDate(e?.target?.value)}
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Travelers */}
              <div>
                <Select
                  label="Travelers"
                  options={travelerOptions}
                  value={travelers}
                  onChange={setTravelers}
                  className="w-full"
                />
              </div>

              {/* Advanced Search Toggle */}
              <div className="md:col-span-2 flex items-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  iconName={showAdvancedSearch ? "ChevronUp" : "ChevronDown"}
                  iconPosition="right"
                  className="text-primary hover:text-primary/80"
                >
                  Advanced Filters
                </Button>
              </div>
            </div>

            {/* Advanced Search Options */}
            {showAdvancedSearch && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-muted rounded-lg animate-slide-up">
                <div>
                  <Select
                    label="Category"
                    options={categoryOptions}
                    value={category}
                    onChange={setCategory}
                    placeholder="Select category"
                  />
                </div>
                <div>
                  <Select
                    label="Price Range"
                    options={priceRangeOptions}
                    value={priceRange}
                    onChange={setPriceRange}
                    placeholder="Select price range"
                  />
                </div>
              </div>
            )}

            {/* Search Button */}
            <div className="flex justify-center">
              <Button
                type="submit"
                size="lg"
                iconName="Search"
                iconPosition="left"
                className="px-12 py-4 text-lg font-semibold"
              >
                Search Tours
              </Button>
            </div>
          </form>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto">
          {[
            { icon: 'MapPin', value: '500+', label: 'Destinations' },
            { icon: 'Users', value: '50K+', label: 'Happy Travelers' },
            { icon: 'Star', value: '4.9', label: 'Average Rating' },
            { icon: 'Award', value: '100+', label: 'Tour Partners' }
          ]?.map((stat, index) => (
            <div key={index} className="text-center text-white animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-full mb-3">
                <Icon name={stat?.icon} size={24} color="white" />
              </div>
              <div className="text-2xl font-bold mb-1">{stat?.value}</div>
              <div className="text-sm text-white/80">{stat?.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;